/**
 * Palette des champs disponibles pour drag-and-drop sur le canvas
 */

import { useDrag } from 'react-dnd';
import { AvailableField, FieldConfig, FREETEXT_FIELDS, PRODUCT_FIELDS, LOT_FIELDS, PRICE_FIELDS, COMPUTED_FIELDS } from '../../types/label-templates.types';

interface Props {
  activeFields: FieldConfig[];
}

export function FieldPalette({ activeFields }: Props) {
  const activeFieldIds = new Set(activeFields.filter((f) => f.enabled).map((f) => f.id));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">
        Champs Disponibles
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Glissez-déposez les champs sur le canvas pour les ajouter à l'étiquette
      </p>

      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {/* Champs de texte libre */}
        <div>
          <h4 className="text-xs font-semibold text-indigo-700 mb-1 uppercase tracking-wide">Texte Libre</h4>
          <div className="space-y-1">
            {FREETEXT_FIELDS.map((field) => (
              <DraggableFieldItem
                key={field.id}
                field={field}
                isActive={false}
              />
            ))}
          </div>
        </div>

        {/* Champs produit */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Produit</h4>
          <div className="space-y-1">
            {PRODUCT_FIELDS.map((field) => (
              <DraggableFieldItem
                key={field.id}
                field={field}
                isActive={activeFieldIds.has(field.id)}
              />
            ))}
          </div>
        </div>

        {/* Champs lot */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Lot</h4>
          <div className="space-y-1">
            {LOT_FIELDS.map((field) => (
              <DraggableFieldItem
                key={field.id}
                field={field}
                isActive={activeFieldIds.has(field.id)}
              />
            ))}
          </div>
        </div>

        {/* Champs de prix */}
        <div>
          <h4 className="text-xs font-semibold text-green-700 mb-1 uppercase tracking-wide">💰 Prix</h4>
          <div className="space-y-1">
            {PRICE_FIELDS.map((field) => (
              <DraggableFieldItem
                key={field.id}
                field={field}
                isActive={activeFieldIds.has(field.id)}
              />
            ))}
          </div>
        </div>

        {/* Champs calculés */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Champs Calculés</h4>
          <div className="space-y-1">
            {COMPUTED_FIELDS.map((field) => (
              <DraggableFieldItem
                key={field.id}
                field={field}
                isActive={activeFieldIds.has(field.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface DraggableFieldItemProps {
  field: AvailableField;
  isActive: boolean;
}

function DraggableFieldItem({ field, isActive }: DraggableFieldItemProps) {
  const [{ isDragging }, drag] = useDrag({
    type: 'NEW_FIELD',
    item: { fieldId: field.id },
    canDrag: !isActive, // Ne peut drag que si pas déjà actif
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'qr':
        return '⬛';
      case 'barcode':
        return '|||';
      case 'badge':
        return '🏷️';
      case 'date':
        return '📅';
      case 'currency':
        return '💰';
      case 'number':
        return '#';
      case 'boolean':
        return '✓';
      default:
        return 'T';
    }
  };

  return (
    <div
      ref={!isActive ? drag : undefined}
      className={`
        flex items-center justify-between p-2 rounded-md border transition-all
        ${
          isActive
            ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-50'
            : isDragging
            ? 'bg-indigo-100 border-indigo-400 opacity-50 cursor-grabbing'
            : 'bg-white border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 cursor-grab'
        }
      `}
      title={isActive ? 'Champ déjà ajouté au canvas' : 'Glissez-déposez sur le canvas'}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm">{getTypeIcon(field.type)}</span>
        <div>
          <div className="text-xs font-medium text-gray-900">{field.label}</div>
          <div className="text-xs text-gray-500">
            {field.source} • {field.type}
          </div>
        </div>
      </div>

      {isActive && (
        <span className="text-xs text-green-600 font-medium">✓ Actif</span>
      )}
    </div>
  );
}
